import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  MicOff, Loader2, Trash2, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Minus, BarChart3, Clock, MapPin,
  Users, AlertTriangle, ClipboardCheck
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isForcedUserView, setForcedUserView } from "@/lib/effectiveClinician";
import VoiceMoodDashboard from "@/components/voicemood/VoiceMoodDashboard";
import StructuredCheckIn from "@/components/voicemood/StructuredCheckIn";

const MOOD_EMOJI = { 1: "\u{1F622}", 2: "\u{1F622}", 3: "\u{1F615}", 4: "\u{1F615}", 5: "\u{1F610}", 6: "\u{1F642}", 7: "\u{1F642}", 8: "\u{1F604}", 9: "\u{1F929}", 10: "\u{1F929}" };
const MOOD_LABEL = { 1: "Pessimo", 2: "Molto male", 3: "Male", 4: "Non bene", 5: "Mediocre", 6: "Bene", 7: "Molto bene", 8: "Benissimo", 9: "Fantastico", 10: "Eccezionale" };

const ACTIVITY_LABELS = {
  lavoro: "Lavoro", studio: "Studio", pausa: "Pausa", socialita: "Socialit\u00e0",
  sport: "Sport", cura_personale: "Cura personale", commissioni: "Commissioni", altro: "Altro"
};
const LOCATION_LABELS = {
  casa: "Casa", lavoro_scuola: "Lavoro/Scuola", fuori: "Fuori", in_movimento: "In movimento"
};
const LOAD_LABELS = { leggero: "Leggera", medio: "Normale", pesante: "Pesante" };
const PLEASANTNESS_LABELS = { spiacevole: "Spiacevole", neutra: "Neutra", piacevole: "Piacevole" };
const VOLUNTARINESS_LABELS = { si: "Sì", misto: "Più o meno", no: "No" };
const SOCIAL_QUALITY_LABELS = { positiva: "Positiva", neutra: "Neutra", tesa: "Tesa", assente: "Non interagivo" };
// 2026-05-30 — FIX: previously the badge defaulted EVERY non-"con_altri" value to "Solo",
// which made all check-ins with partner/famiglia/amici/colleghi look like "Solo" in the
// recent-entries feed. Saved data was correct, only the label was wrong.
const SOCIAL_CONTEXT_LABELS = {
  solo: "Solo", amici: "Amici", partner: "Partner", famiglia: "Famiglia",
  colleghi: "Colleghi", superiori: "Superiori", clienti: "Clienti", altri: "Altri",
  partner_famiglia: "Partner/Famiglia", con_altri: "Con altri",
};
const DERIVED_LABEL_STYLES = {
  stressante: "border-red-400 bg-red-50 text-red-700",
  rilassante: "border-green-400 bg-green-50 text-green-700",
  impegnativa: "border-amber-400 bg-amber-50 text-amber-700",
  conflittuale: "border-orange-500 bg-orange-50 text-orange-700",
};

// ===== ENTRY CARD =====
const VoiceMoodEntry = ({ entry, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const v = entry.variables || {};
  const mood = v.mood_level;
  const time = entry.recorded_at ? new Date(entry.recorded_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
  const mode = entry.mode || "quick";
  const qualityScore = entry.quality_score ?? null;
  const isLowQuality = mode === "quick" && qualityScore !== null && qualityScore < 2;
  const derivedLabels = v.derived_labels || [];

  return (
    <Card
      className={`border shadow-sm ${isLowQuality ? "border-amber-300 bg-amber-50/30" : ""}`}
      data-testid={`voice-entry-${entry.entry_id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{MOOD_EMOJI[mood] || "\u{1F610}"}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-sm">{MOOD_LABEL[mood] || "?"}</span>
              <Badge
                variant={mode === "structured" ? "default" : "secondary"}
                className={`text-[10px] ${mode === "structured" ? "bg-rose-500" : ""}`}
              >
                <ClipboardCheck className="w-2.5 h-2.5 mr-0.5" />
                {mode === "structured" ? "Strutturato" : "Legacy"}
              </Badge>
              {isLowQuality && (
                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Qualità bassa
                </Badge>
              )}
              <Badge variant="secondary" className="text-[10px]">{entry.time_of_day}</Badge>
              <span className="text-xs text-muted-foreground">{time}</span>
            </div>
            {/* Derived labels (stressante/rilassante/impegnativa/conflittuale) */}
            {derivedLabels.length > 0 && (
              <div className="flex gap-1 mt-1 flex-wrap" data-testid="entry-derived-labels">
                {derivedLabels.map((label) => (
                  <Badge key={label} variant="outline" className={`text-[10px] font-medium ${DERIVED_LABEL_STYLES[label] || ""}`}>
                    {label}
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-1.5 mt-1 text-xs text-muted-foreground flex-wrap">
              {v.activity && <Badge variant="outline" className="text-[10px] font-normal">{ACTIVITY_LABELS[v.activity] || v.activity}{v.activity_detail ? `: ${v.activity_detail}` : ""}</Badge>}
              {v.location && v.location !== "null" && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{LOCATION_LABELS[v.location] || v.location}</span>}
              {v.social_context && v.social_context !== "null" && (
                <span className="flex items-center gap-0.5" data-testid={`entry-social-${v.social_context}`}>
                  <Users className="w-3 h-3" />
                  {v.social_context === "con_altri"
                    ? (v.social_detail || "Con altri")
                    : (SOCIAL_CONTEXT_LABELS[v.social_context] || v.social_context)}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 text-muted-foreground hover:text-foreground">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button onClick={() => onDelete(entry.entry_id)} className="p-1.5 text-muted-foreground hover:text-red-600" data-testid={`delete-entry-${entry.entry_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-2 text-sm">
            {/* 4 EMA dimensions (if structured) */}
            {mode === "structured" && (
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                {v.load && <div><span className="text-muted-foreground">Carico:</span> <span className="font-medium">{LOAD_LABELS[v.load] || v.load}</span></div>}
                {v.pleasantness && <div><span className="text-muted-foreground">Piacevolezza:</span> <span className="font-medium">{PLEASANTNESS_LABELS[v.pleasantness] || v.pleasantness}</span></div>}
                {v.voluntariness && <div><span className="text-muted-foreground">Volontarietà:</span> <span className="font-medium">{VOLUNTARINESS_LABELS[v.voluntariness] || v.voluntariness}</span></div>}
                {v.social_quality && <div><span className="text-muted-foreground">Qualità sociale:</span> <span className="font-medium">{SOCIAL_QUALITY_LABELS[v.social_quality] || v.social_quality}</span></div>}
              </div>
            )}
            {v.mood_description && <p className="italic text-muted-foreground">"{v.mood_description}"</p>}
            {v.causal_factors?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {v.causal_factors.map((cf, i) => (
                  <Badge key={`${cf.factor}-${i}`} variant={cf.valence === "positivo" ? "outline" : "destructive"} className="text-[10px]">
                    {cf.valence === "positivo" ? "+" : "-"} {cf.factor}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== CORRELATION CARD =====
const CorrelationItem = ({ c }) => {
  const varLabels = {
    attivita: "Attivit\u00e0", luogo: "Luogo", contesto: "Contesto",
    momento: "Momento", stress: "Stress",
    esperienza: "Esperienza", fattore: "Fattore",
    carico: "Carico", piacevolezza: "Piacevolezza", volonta: "Volontarietà",
    qualita_sociale: "Qualità sociale", carattere: "Carattere",
  };
  const icon = c.impact === "positivo" ? <TrendingUp className="w-4 h-4 text-green-600" /> :
    c.impact === "negativo" ? <TrendingDown className="w-4 h-4 text-red-500" /> :
    <Minus className="w-4 h-4 text-gray-400" />;

  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-lg ${c.impact === "positivo" ? "bg-green-50" : c.impact === "negativo" ? "bg-red-50" : "bg-muted/50"}`}>
      {icon}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{varLabels[c.variable] || c.variable}: <span className="font-normal">{c.value}</span></p>
        <p className="text-xs text-muted-foreground">{c.count} registrazioni, umore medio {c.avg_mood}</p>
      </div>
      <Badge variant={c.impact === "positivo" ? "outline" : c.impact === "negativo" ? "destructive" : "secondary"} className="text-xs">
        {c.diff_from_overall > 0 ? "+" : ""}{c.diff_from_overall.toFixed(1)}
      </Badge>
    </div>
  );
};

// ===== MAIN PAGE =====
const VoiceMoodPage = ({ initialTab = null }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [correlations, setCorrelations] = useState(null);
  const [showResult, setShowResult] = useState(null);
  const [hasAccess, setHasAccess] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab === "dashboard" ? "dashboard" : "record");
  // 2026-06: vista forzata "come utente" per Laura (admin/clinico)
  const [realIsClinician, setRealIsClinician] = useState(false);
  const [forcedUserView, setForcedUserViewState] = useState(isForcedUserView());

  useEffect(() => {
    checkAccess();
    api.get("/auth/me").then((r) => setRealIsClinician(!!r.data?.is_clinician)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleForcedUserView = () => {
    const next = !forcedUserView;
    setForcedUserView(next);
    setForcedUserViewState(next);
    // 2026-06 fix: invece di window.location.reload() (che fa rimbalzare via
    // BenesserePage), notifico i sub-pannelli con un evento custom così
    // si ri-fetchano /auth/me senza ricaricare la pagina.
    window.dispatchEvent(new CustomEvent("revela:forced-user-view-changed", {
      detail: { forced: next },
    }));
  };

  const checkAccess = async () => {
    try {
      const res = await api.get("/voice-mood/access");
      setHasAccess(res.data.has_access);
      if (res.data.has_access) {
        loadEntries();
        loadCorrelations();
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[VoiceMood] access check failed:", e?.message);
      setHasAccess(false);
    }
  };

  const loadEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get("/voice-mood/entries?days=30");
      setEntries(res.data || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[VoiceMood] loadEntries failed:", e?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCorrelations = async () => {
    try {
      const res = await api.get("/voice-mood/correlations?days=30");
      setCorrelations(res.data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[VoiceMood] loadCorrelations failed:", e?.message);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await api.delete(`/voice-mood/entries/${entryId}`);
      setEntries((prev) => prev.filter((e) => e.entry_id !== entryId));
      toast.success("Eliminata");
      loadCorrelations();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (hasAccess === null) {
    return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  if (!hasAccess) {
    return (
      <div className="text-center py-16 space-y-3">
        <MicOff className="w-12 h-12 text-muted-foreground/40 mx-auto" />
        <h2 className="text-lg font-semibold">Funzione in fase sperimentale</h2>
        <p className="text-sm text-muted-foreground">Il Check-in non è ancora disponibile per il tuo account.</p>
      </div>
    );
  }

  // 2026-06: Quando l'utente arriva da "Le mie scoperte" (initialTab='dashboard'),
  // mostra SOLO la dashboard: niente tab "Nuovo / Dashboard", niente header
  // "Check-in", titolo dedicato.
  const isDiscoveriesMode = initialTab === "dashboard";

  return (
    <div className="space-y-4 pb-24 md:pb-8" data-testid="voice-mood-page">
      {/* Header */}
      {isDiscoveriesMode ? (
        <div className="space-y-1">
          <h1
            className="text-xl sm:text-2xl font-bold"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Le mie scoperte
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Cosa stiamo capendo di te, in base ai tuoi check-in.
          </p>
          {/* 2026-06: toggle vista utente (solo per Laura/admin).
              Permette di vedere la dashboard esattamente come la vede una beta tester. */}
          {realIsClinician && (
            <div
              className={`mt-2 flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                forcedUserView
                  ? "border-amber-300 bg-amber-50/70 text-amber-900"
                  : "border-stone-200 bg-stone-50/60 text-stone-700"
              }`}
              data-testid="forced-user-view-toggle"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {forcedUserView ? "👁 Vista utente attiva" : "Modalità clinico"}
                </p>
                <p className="text-[10px] opacity-80 leading-tight">
                  {forcedUserView
                    ? "Stai vedendo la dashboard come una beta tester. Approfondimenti clinici nascosti."
                    : "Stai vedendo tutto, inclusi approfondimenti clinici."}
                </p>
              </div>
              <button
                type="button"
                onClick={toggleForcedUserView}
                className={`shrink-0 text-[11px] font-medium rounded px-2.5 py-1 border transition-colors ${
                  forcedUserView
                    ? "border-amber-400 bg-white hover:bg-amber-50 text-amber-900"
                    : "border-stone-300 bg-white hover:bg-stone-100"
                }`}
                data-testid="forced-user-view-toggle-btn"
              >
                {forcedUserView ? "Torna alla vista clinico" : "Vedi come utente"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-bold">Check-in</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Descrivi un momento per scoprire i tuoi pattern.</p>
        </div>
      )}

      {/* Tabs — nascosti in "Le mie scoperte" */}
      {!isDiscoveriesMode && (
      <div className="flex gap-1 bg-muted/50 p-1 rounded-lg" data-testid="voice-mood-tabs">
        {[
          { id: "record", label: "Nuovo" },
          { id: "dashboard", label: "Dashboard" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-1.5 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {activeTab === "dashboard" ? (
        <VoiceMoodDashboard />
      ) : (
        <>
          <StructuredCheckIn
            onComplete={(data) => {
              setShowResult(data);
              loadEntries();
              loadCorrelations();
            }}
            onCancel={() => { /* no-op: nothing to cancel to */ }}
          />

          {/* Result Dialog */}
          <Dialog open={!!showResult} onOpenChange={() => setShowResult(null)}>
            <DialogContent className="max-w-sm" data-testid="voice-result-dialog">
              <DialogHeader>
                <DialogTitle>Check-in salvato</DialogTitle>
              </DialogHeader>
              {showResult && (
                <div className="space-y-3">
                  <div className="text-center">
                    <span className="text-4xl">{MOOD_EMOJI[showResult.variables?.mood_level]}</span>
                    <p className="font-semibold mt-1">{MOOD_LABEL[showResult.variables?.mood_level]}</p>
                  </div>
                  {showResult.derived_labels?.length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-center">
                      {showResult.derived_labels.map((label) => (
                        <Badge key={label} variant="outline" className={`text-xs ${DERIVED_LABEL_STYLES[label] || ""}`}>
                          {label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground text-center">
                    Classificazione derivata automaticamente dalle dimensioni EMA
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Correlations */}
          {correlations && correlations.correlations?.length > 0 && (
            <Card className="border shadow-sm" data-testid="correlations-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-500" />
                  Pattern del tuo umore
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 text-sm mb-2 flex-wrap">
                  <span className="text-muted-foreground">Umore medio:</span>
                  <span className="text-2xl">{MOOD_EMOJI[Math.round(correlations.overall_avg_mood)]}</span>
                  <span className="font-semibold">{correlations.overall_avg_mood}/10</span>
                  <Badge
                    variant="secondary"
                    className="text-xs"
                    title="Check-in affidabili usati per le correlazioni / check-in totali"
                    data-testid="reliable-counter"
                  >
                    {correlations.reliable_count ?? correlations.total_entries} affidabili su {correlations.total_count ?? correlations.total_entries} totali
                  </Badge>
                </div>
                {correlations.insights?.length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-3 space-y-1.5">
                    {correlations.insights.map((insight) => (
                      <p key={`insight-${insight.substring(0, 20)}`} className="text-sm text-indigo-900">{insight}</p>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  {correlations.correlations.slice(0, 6).map((c) => (
                    <CorrelationItem key={`${c.variable}-${c.value}`} c={c} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Entries */}
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Registrazioni recenti
            </h2>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : entries.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <ClipboardCheck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nessun check-in ancora. Compila il primo qui sopra!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {entries.map((entry) => (
                  <VoiceMoodEntry key={entry.entry_id} entry={entry} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VoiceMoodPage;
